import React from 'react';
import { CalendarBlank, Users, ListBullets, Warning } from '@phosphor-icons/react';

interface CycleItem {
    cycle_name?: string;
    cycleName?: string;
    cycle_type?: string;
    cycleType?: string;
    cycle_status?: string;
    cycleStatus?: string;
    start_date?: string;
    startDate?: string;
    end_date?: string;
    endDate?: string;
    participants?: number;
    completion_percentage?: number;
    completionPercentage?: number;
    is_at_risk?: boolean;
    isAtRisk?: boolean;
}

interface CycleCardProps {
    data: CycleItem[] | { data: CycleItem[]; metadata?: any; meta?: any; schema?: any };
    options?: {
        showCompletion?: boolean;
        showType?: boolean;
        showDateRange?: boolean;
        showParticipants?: boolean;
        maxCards?: number;
    };
}

const getCycleTypeConfig = (type: string) => {
    const t = (type || '').toLowerCase();
    if (t === 'standard' || t.includes('performance')) return { color: 'bg-violet-100 text-violet-700', label: 'Performance' };
    if (t.includes('360')) return { color: 'bg-cyan-100 text-cyan-700', label: '360 Review' };
    if (t.includes('check')) return { color: 'bg-teal-100 text-teal-700', label: 'Check-In' };
    if (t.includes('annual') || t.includes('yearly')) return { color: 'bg-purple-100 text-purple-700', label: 'Annual' };
    if (t.includes('quarterly')) return { color: 'bg-blue-100 text-blue-700', label: 'Quarterly' };
    if (t.includes('probation')) return { color: 'bg-orange-100 text-orange-700', label: 'Probation' };
    if (t.includes('pip')) return { color: 'bg-red-100 text-red-700', label: 'PIP' };
    return { color: 'bg-gray-100 text-gray-600', label: type || 'Review' };
};

const getStatusConfig = (status: string) => {
    const s = (status || '').toLowerCase().replace(/_/g, ' ');
    if (s.includes('complete')) return { color: 'bg-green-100 text-green-700 border border-green-200', label: 'Completed' };
    if (s.includes('active') || s.includes('progress') || s.includes('ongoing')) return { color: 'bg-blue-100 text-blue-700 border border-blue-200', label: 'Active' };
    if (s.includes('draft') || s.includes('new')) return { color: 'bg-gray-100 text-gray-600 border border-gray-200', label: 'Draft' };
    if (s.includes('closed') || s.includes('archived')) return { color: 'bg-slate-100 text-slate-600 border border-slate-200', label: 'Closed' };
    return { color: 'bg-gray-100 text-gray-600 border border-gray-200', label: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown' };
};

const formatDate = (dateStr?: string | null): string | null => {
    if (!dateStr) return null;
    try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return null;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
        return null;
    }
};

export const CycleCard: React.FC<CycleCardProps> = ({ data, options = {} }) => {
    const {
        showCompletion = true,
        showType = true,
        showDateRange = true,
        showParticipants = true,
        maxCards = 10,
    } = options;

    let items: CycleItem[] = [];
    if (Array.isArray(data)) {
        items = data;
    } else if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        items = data.data;
    }

    const displayItems = items.slice(0, maxCards);

    if (!displayItems || displayItems.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-6">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    <ListBullets size={28} className="text-gray-400" />
                </div>
                <span className="text-sm font-medium">No cycles found</span>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2.5 p-3 h-full overflow-auto">
            {displayItems.map((item, index) => {
                const name = item.cycleName || item.cycle_name || 'Unnamed Cycle';
                const type = item.cycleType || item.cycle_type || '';
                const status = item.cycleStatus || item.cycle_status || '';
                const startDate = formatDate(item.startDate || item.start_date);
                const endDate = formatDate(item.endDate || item.end_date);
                const participants = item.participants;
                const completion = item.completionPercentage ?? item.completion_percentage;
                const atRisk = item.isAtRisk ?? item.is_at_risk;

                const typeConfig = getCycleTypeConfig(type);
                const statusConfig = getStatusConfig(status);

                return (
                    <div
                        key={`cycle-${index}`}
                        className="group bg-white rounded-lg border border-gray-200 p-3.5 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
                    >
                        {/* Row 1: Name + At Risk badge */}
                        <div className="flex items-start justify-between gap-3 mb-2.5">
                            <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 group-hover:text-blue-600 transition-colors flex-1">
                                {name}
                            </h3>
                            {atRisk && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-red-100 text-red-700 border border-red-200 flex-shrink-0">
                                    <Warning size={10} weight="bold" />
                                    At Risk
                                </span>
                            )}
                        </div>

                        {/* Row 2: Badges */}
                        <div className="flex items-center gap-2 flex-wrap mb-3">
                            {showType && type && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${typeConfig.color}`}>
                                    {typeConfig.label}
                                </span>
                            )}
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${statusConfig.color}`}>
                                {statusConfig.label}
                            </span>
                        </div>

                        {/* Row 3: Completion Progress */}
                        {showCompletion && completion !== undefined && completion !== null && (
                            <div className="flex items-center gap-3 mb-3">
                                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-500 rounded-full transition-all duration-500"
                                        style={{ width: `${Math.min(completion, 100)}%` }}
                                    />
                                </div>
                                <span className="text-xs font-medium text-gray-600 min-w-[32px] text-right">
                                    {Math.round(completion)}%
                                </span>
                            </div>
                        )}

                        {/* Row 4: Footer */}
                        <div className="flex items-center justify-between text-xs text-gray-500 pt-2.5 border-t border-gray-100">
                            {showDateRange && (startDate || endDate) ? (
                                <div className="flex items-center gap-1.5 min-w-0">
                                    <CalendarBlank size={14} className="text-gray-400 flex-shrink-0" />
                                    <span className="truncate">
                                        {startDate && endDate ? `${startDate} - ${endDate}` : startDate || endDate}
                                    </span>
                                </div>
                            ) : (
                                <div />
                            )}
                            {showParticipants && participants !== undefined && participants !== null && (
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                    <Users size={14} className="text-gray-400" />
                                    <span>{participants}</span>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
